import { GooglePlacesController } from "@controllers/Google-places.controller";
import {
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FC } from "react";

interface LocationResult {
  locationName: string;
  latLng: google.maps.LatLngLiteral;
}
type FormGoogleAutocompleteProps = TextFieldProps & {
  /**
   * Returns the `locationName` and `google.maps.LatLngLiteral` object of the first result from the
   * search in `google.maps.places API` -  by the value the user has filled.
   */
  placeValue: string;
  onLocationChange: (location: LocationResult) => void;
  /** Include landmarks (hospitals, stadiums, etc.) alongside street addresses. */
  includeEstablishments?: boolean;
};
const defaultLatLng = { lat: 0, lng: 0 };
export const FormGoogle: FC<FormGoogleAutocompleteProps> = (props) => {
  const {
    onLocationChange: setLocationValue,
    placeValue,
    includeEstablishments = true,
    ...inputProps
  } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [resultName, setResultName] = useState<string>();
  const [results, setResults] = useState<
    google.maps.places.AutocompletePrediction[] | null
  >(null);
  const [inputValue, setInputValue] = useState<string>("");

  useEffect(() => {
    setInputValue(placeValue || "");
  }, [placeValue]);

  useEffect(() => {
    if (!inputValue || inputValue === resultName || inputValue === placeValue)
      return setResults(null);
    const timeout = setTimeout(() => {
      getLocationMutation.mutate(inputValue);
    }, 500);
    return () => clearTimeout(timeout);
  }, [inputValue]);

  const getLocationMutation = useMutation({
    mutationKey: ["get-google-location", inputValue],
    mutationFn: getLocation,
    onSuccess: (data) => data && setResults(data),
    gcTime: Infinity,
  });

  async function getLocation(query: string) {
    if (query && query.length >= 2) {
      return await GooglePlacesController.getAutocomplete(query, {
        includeEstablishments,
      });
    }
  }

  function removeIsraelFromLocation(description: string): string {
    // Remove ", ישראל" or "ישראל" from the end of the string
    return description.replace(/,\s*ישראל\s*$/i, "").replace(/\s*ישראל\s*$/i, "").trim();
  }

  async function handleClick(
    result: google.maps.places.AutocompletePrediction,
  ) {
    if (result.description) {
      const location = await GooglePlacesController.getPlaceCoordinates(
        result.place_id,
      );
      // Remove "ישראל" from the location name
      const locationName = removeIsraelFromLocation(result.description);
      setLocationValue({
        latLng: location ?? defaultLatLng,
        locationName,
      });
      setInputValue(locationName);
      setResults(null);
      setResultName(locationName);
    }
  }

  return (
    <>
      <TextField
        value={inputValue || ""}
        inputRef={setAnchorEl}
        {...inputProps}
        onChange={(event) => setInputValue(event.target.value)}
      />
      {results && results?.length > 0 && (
        <Menu
          onClose={() => setResults(null)}
          anchorEl={anchorEl}
          autoFocus={false}
          disableAutoFocusItem
          disableAutoFocus
          disableEnforceFocus
          open
        >
          {results?.map((result) => (
            <MenuItem
              sx={{ width: anchorEl?.clientWidth! + 35 }}
              key={result.place_id}
              onClick={() => handleClick(result)}
            >
              {result.place_id ? (
                <Stack
                  direction="column"
                  sx={{
                    overflow: "hidden", // Ensure stack content follows the overflow rule
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Typography
                    fontSize="medium"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {result.description}
                  </Typography>
                </Stack>
              ) : (
                <Typography
                  fontSize="medium"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {result.description}
                </Typography>
              )}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};
